/**
 * سجل رسائل الإدارة — سجل دائم لكل رسالة يرسلها الأدمن للمستخدمين.
 *
 * كل عملية إرسال تُنتج مستنداً واحداً في `admin_messages` يحتوي على:
 * - هوية الأدمن المرسل (uid + بريده)
 * - نص الرسالة وعنوانها ونوعها
 * - لقطة من الفلاتر المستخدمة وقت الإرسال (حتى نعرف لماذا وصلت الرسالة لهؤلاء)
 * - قائمة المستلمين (الاسم/الهاتف/النوع) مع عدد الناجحين والفاشلين وأسباب الفشل
 *
 * يُنشأ المستند قبل الإرسال ليحمل كل إشعار `data.messageLogId` لربط الإشعار بسجله،
 * ثم يُحدَّث بعد الانتهاء بحالة الإرسال الفعلية.
 */
import {
  addDoc,
  collection,
  doc,
  type DocumentReference,
  Timestamp,
  updateDoc,
} from 'firebase/firestore'
import { db } from '../firebase'

export const ADMIN_MESSAGES_COLLECTION = 'admin_messages'

export type AdminMessageType = 'notification' | 'email'
export type AdminMessageStatus = 'sending' | 'sent' | 'partial' | 'failed'

/** أقصى عدد مستلمين تُحفظ تفاصيلهم داخل المستند (حد Firestore 1MB) */
export const MAX_STORED_RECIPIENTS = 400

export interface AdminMessageRecipient {
  userId: string
  name: string
  phone: string
  userType: string
  status: 'sent' | 'failed'
  error?: string
}

export interface AdminMessageFilterSnapshot {
  type?: string
  status?: string
  search?: string
  categoryKeys?: string[]
  categoryNames?: string[]
  includeAllowedCategories?: boolean
  matchedCount?: number
}

export interface AdminMessageLogDoc {
  title: string
  body: string
  messageType: AdminMessageType
  adminId: string
  adminEmail: string
  source: string
  sourceUrl: string
  status: AdminMessageStatus
  recipientIds: string[]
  recipientCount: number
  successCount: number
  failedCount: number
  recipients: AdminMessageRecipient[]
  recipientsTruncated: boolean
  filters: AdminMessageFilterSnapshot
  createdAt?: any
  completedAt?: any
}

/** لقطة مستلم قبل knowing النتيجة — تُستعمل عند إنشاء السجل */
export type RecipientDraft = Omit<AdminMessageRecipient, 'status' | 'error'>

export function computeMessageStatus(successCount: number, failedCount: number): AdminMessageStatus {
  if (failedCount === 0) return 'sent'
  return successCount === 0 ? 'failed' : 'partial'
}

/**
 * يزيل كل الحقول التي قيمتها `undefined` (بعمق).
 *
 * إلزامي: Firestore يرفض `undefined` افتراضياً (ignoreUndefinedProperties = false) فيرمي
 * "Cannot use 'undefined' as a Firestore value" — فلولا هذا التنظيف لأخفى فشل الكتابة
 * نفسه ورفض تسجيل الرسالة.
 */
export function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item)) as unknown as T
  }
  if (value === null || typeof value !== 'object') return value
  // Timestamp و DocumentReference و أي كائن من الفirebase يُمرَّر كما هو
  if (typeof (value as any).toDate === 'function' || typeof (value as any).path === 'string') {
    return value
  }
  const out: Record<string, any> = {}
  for (const [key, val] of Object.entries(value as Record<string, any>)) {
    if (val === undefined) continue
    out[key] = stripUndefined(val)
  }
  return out as T
}

/**
 * ينشئ مستند سجل الرسالة ويعيد مرجعه (حتى يُربط كل إشعار به).
 * يعيد null عند الفشل حتى لا يُفشل الإرسال نفسه — لكن مع إرجاع سبب الفشل ليُعرض على الأدمن.
 */
export async function createAdminMessageLog(params: {
  title: string
  body: string
  messageType: AdminMessageType
  adminId: string
  adminEmail: string
  source: string
  sourceUrl: string
  recipients: RecipientDraft[]
  filters: AdminMessageFilterSnapshot
}): Promise<{
  ref: DocumentReference | null
  recipients: AdminMessageRecipient[]
  error?: string
}> {
  const recipients: AdminMessageRecipient[] = params.recipients.map((r) => ({ ...r, status: 'sent' }))
  const payload = stripUndefined({
    title: params.title.trim(),
    body: params.body.trim(),
    messageType: params.messageType,
    adminId: params.adminId,
    adminEmail: params.adminEmail,
    source: params.source,
    sourceUrl: params.sourceUrl,
    status: 'sending' as AdminMessageStatus,
    recipientIds: recipients.map((r) => r.userId),
    recipientCount: recipients.length,
    successCount: 0,
    failedCount: 0,
    recipients: recipients.slice(0, MAX_STORED_RECIPIENTS),
    recipientsTruncated: recipients.length > MAX_STORED_RECIPIENTS,
    filters: params.filters,
    createdAt: Timestamp.now(),
  })

  try {
    const ref = await addDoc(collection(db, ADMIN_MESSAGES_COLLECTION), payload)
    return { ref, recipients }
  } catch (error: any) {
    console.error('فشل في حفظ سجل الرسالة:', error)
    return { ref: null, recipients, error: error?.message || 'خطأ غير معروف' }
  }
}

/** يحدّث سجل الرسالة بالنتائج الفعلية بعد انتهاء الإرسال */
export async function finalizeAdminMessageLog(
  ref: DocumentReference | null,
  recipients: AdminMessageRecipient[]
): Promise<void> {
  if (!ref) return
  const successCount = recipients.filter((r) => r.status === 'sent').length
  const failedCount = recipients.length - successCount
  try {
    await updateDoc(doc(db, ADMIN_MESSAGES_COLLECTION, ref.id), {
      status: computeMessageStatus(successCount, failedCount),
      successCount,
      failedCount,
      recipients: stripUndefined(recipients.slice(0, MAX_STORED_RECIPIENTS)),
      recipientsTruncated: recipients.length > MAX_STORED_RECIPIENTS,
      completedAt: Timestamp.now(),
    })
  } catch (error) {
    console.error('فشل في تحديث سجل الرسالة:', error)
  }
}
